/*

? The chat drawer's list screen (rebuilt 18 Oct 2026 to the app's Chat tab, bbm-android ChatScreen).

  - The acting profile's header, then "Chats".
  - Search once there are more than 5 chats (the app's rule, counted with no search term), on the
    server, from the first character.
  - The chips: All, Unread, Requirements, Broadcasts, filtering the rows loaded, hidden while
    searching and when there are no chats. Pending broadcast invites are rows of the list (under All
    and Broadcasts): the separate Requests tab is gone, as in the app. A link that asks for the
    broadcasts (`?chat_tab=broadcasts`, a broadcast notification) opens on the Broadcasts chip.

*/

/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { setChatListTabThunk } from "../../../../redux-magic/store";

/* Component Imports */

import { Typography } from "@mui/material";
import ChatList, { ChatListFilter, chatListFilterOf } from "./ChatList";
import ChatListHeader from "./ChatListHeader";
import ChatSearch from "./ChatSearch";
import StandaloneFilterChip from "../../../common-components/StandaloneFilterChip";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	background: theme.palette.background.paper,
	borderRadius: "0rem",
	height: "100%",
	width: "100%",
	padding: "0rem 0rem 0rem 0rem",
}));

const TitleRow = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: "1rem 1rem 0.5rem 1rem",
}));

const ChatTypography = styled(Typography)(({ theme }) => ({
	fontSize: "1.125rem",
	fontWeight: 600,
	color: "black",
	textAlign: "left",
}));

const ChipRow = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	gap: "0.5rem",
	padding: "0.25rem 1rem 0.5rem 1rem",
	overflowX: "auto",
	scrollbarWidth: "none",
	"&::-webkit-scrollbar": { display: "none" },
}));

const ChatListRoot = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	width: "100%",
	height: "100%",
}));

const ChatListScreen = ({
	session,
	dispatch,
	router,
	profilePicture,
	profile_context,
	chat_list,
	list_tab,
	typing_chat_ids,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	profilePicture: string;
	profile_context: any;
	chat_list: StateInterface["chat_states"]["chat_list"];
	list_tab: string;
	// Conversations whose other side is typing right now (18 Oct 2026).
	typing_chat_ids?: Array<string>;
}) => {

	/* Translations */

	const ar: boolean = router.locale === "ar-AE";
	const kn: boolean = router.locale === "kn-IN";
	const localLang = {
		chats: ar ? "الدردشات" : kn ? "ಚಾಟ್‌ಗಳು" : "Chats",
		unread: ar ? "غير مقروءة" : kn ? "ಓದದ" : "Unread",
		requirements: ar ? "المتطلبات" : kn ? "ಅವಶ್ಯಕತೆಗಳು" : "Requirements",
		broadcasts: ar ? "البث" : kn ? "ಪ್ರಸಾರಗಳು" : "Broadcasts",
	};

	const [clearAutoComplete, setClearAutoComplete] = React.useState<boolean>(false);
	const [searchInputValue, setSearchInputValue] = React.useState<string>("");

	const filter: ChatListFilter = chatListFilterOf(list_tab);
	const searching: boolean = searchInputValue.trim() !== "";
	const allChatsCount: number = Number(chat_list.all_chats_count || 0);

	/* No "All" chip (20 Oct 2026, the app's rule): with none on, every chat shows. */
	const chips: Array<{ key: ChatListFilter; label: string }> = [
		{ key: "unread", label: localLang.unread },
		{ key: "requirements", label: localLang.requirements },
		{ key: "broadcasts", label: localLang.broadcasts },
	];

	return (
		<RootDiv>
			{/* Chat List Header */}

			<ChatListHeader
				session={session}
				dispatch={dispatch}
				router={router}
				profilePicture={profilePicture}
				profile_context={profile_context}
				loading={chat_list.chats_loading}
			/>

			<TitleRow>
				<ChatTypography>{localLang.chats}</ChatTypography>
			</TitleRow>

			<ChatListRoot>
				{/* Search: more than 5 chats (the app's rule), or a search already typed. */}

				{allChatsCount > 5 || searching ? (
					<ChatSearch
						session={session}
						dispatch={dispatch}
						router={router}
						searchInputValue={searchInputValue}
						setSearchInputValue={setSearchInputValue}
						clearAutoComplete={clearAutoComplete}
						setClearAutoComplete={setClearAutoComplete}
					/>
				) : null}

				{/* The chips, the app's contained ones: hidden while searching and with no chats. */}

				{!searching && allChatsCount > 0 ? (
					<ChipRow role="tablist">
						{chips.map((chip) => {
							const selected: boolean = chip.key === filter;
							return (
								<StandaloneFilterChip
									key={chip.key}
									label={chip.label}
									selected={selected}
									onClick={() => dispatch(setChatListTabThunk(selected ? "all" : chip.key))}
								/>
							);
						})}
					</ChipRow>
				) : null}

				<ChatList
					session={session}
					dispatch={dispatch}
					router={router}
					profile_context={profile_context}
					chat_list={chat_list}
					search_term={searchInputValue}
					clearAutoComplete={clearAutoComplete}
					filter={searching ? "all" : filter}
					typing_chat_ids={typing_chat_ids}
				/>
			</ChatListRoot>
		</RootDiv>
	);
};

export default ChatListScreen;
